"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Button } from "@/components/ui/button";
import type { ProgrammaticScrollResult } from "@/components/gift-list-search/use-public-search-motion";
import { getPublicHeaderBottom } from "@/lib/public-header-metrics";

const WALKTHROUGH_STORAGE_KEY = "tb-busca-walkthrough";
const TARGET_PADDING = 10;
const TIP_GAP = 16;
const VIEWPORT_GUTTER = 16;
const MIN_VERTICAL_BAND = 300;

const tourSteps = [
  {
    target: "query",
    eyebrow: "Encontrar uma lista · Passo 1 de 3",
    title: "Comece pelo",
    emphasis: "nome",
    description:
      "Recebeu o link da lista? Ele leva direto a ela. Sem o link, o nome do casal, do anfitrião ou da própria lista abre o caminho.",
  },
  {
    target: "refinements",
    eyebrow: "Encontrar uma lista · Passo 2 de 3",
    title: "Refine a",
    emphasis: "busca",
    description:
      "Nomes parecidos são comuns: a data do evento e a localidade estreitam o resultado até a lista certa.",
  },
  {
    target: "submit",
    eyebrow: "Encontrar uma lista · Passo 3 de 3",
    title: "Confira e",
    emphasis: "presenteie",
    description:
      "Confirme nome e data nos resultados, abra a lista e escolha o presente. Do embrulho à entrega, cuidamos do resto.",
  },
] as const;

interface TourGeometry {
  spotlight: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  tip: {
    top: number;
    left: number;
    width: number;
    height: number;
    align: "flex-start" | "center" | "flex-end";
    placement: "above" | "below" | "side";
    arrowLeft: number;
  };
}

function markWalkthroughSeen(): void {
  try {
    window.localStorage.setItem(WALKTHROUGH_STORAGE_KEY, "1");
  } catch {
    // Storage can be unavailable in privacy-restricted browsing contexts.
  }
}

function afterAnimationFrame(): Promise<void> {
  return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
}

function getStepScrollTarget(step: number): number | null {
  const currentStep = tourSteps[step];
  const target = document.querySelector<HTMLElement>(
    `[data-public-search-tour-target="${currentStep.target}"]`,
  );
  if (target === null) return null;

  const targetRect = target.getBoundingClientRect();
  if (targetRect.height === 0 || targetRect.width === 0) return null;
  const safeTop = getPublicHeaderBottom() + VIEWPORT_GUTTER;
  const safeBottom = window.innerHeight - VIEWPORT_GUTTER;
  const spotlightTop = targetRect.top - TARGET_PADDING;
  const spotlightBottom = targetRect.bottom + TARGET_PADDING;
  const spotlightHeight = spotlightBottom - spotlightTop;
  const safeHeight = Math.max(1, safeBottom - safeTop);

  let delta = 0;
  if (spotlightHeight > safeHeight) {
    delta =
      spotlightTop + spotlightHeight / 2 - (safeTop + safeHeight / 2);
  } else if (spotlightTop < safeTop) {
    delta = spotlightTop - safeTop;
  } else if (spotlightBottom > safeBottom) {
    delta = spotlightBottom - safeBottom;
  }
  if (Math.abs(delta) <= 1) return null;
  return window.scrollY + delta;
}

interface GiftListSearchTourProps {
  cancelProgrammaticScroll(): void;
  scrollToPosition(top: number): Promise<ProgrammaticScrollResult>;
}

export function GiftListSearchTour({
  cancelProgrammaticScroll,
  scrollToPosition,
}: GiftListSearchTourProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [geometry, setGeometry] = useState<TourGeometry | null>(null);
  const autoOpenTimerRef = useRef<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const preparationVersionRef = useRef(0);
  const preparingStepRef = useRef(false);

  const clearAutoOpenTimer = useCallback(() => {
    if (autoOpenTimerRef.current === null) return;
    window.clearTimeout(autoOpenTimerRef.current);
    autoOpenTimerRef.current = null;
  }, []);

  const closeTour = useCallback(() => {
    preparationVersionRef.current += 1;
    preparingStepRef.current = false;
    cancelProgrammaticScroll();
    clearAutoOpenTimer();
    markWalkthroughSeen();
    setGeometry(null);
    setOpen(false);
  }, [cancelProgrammaticScroll, clearAutoOpenTimer]);

  const measure = useCallback(() => {
    if (!open) return;
    const currentStep = tourSteps[step];
    const target = document.querySelector<HTMLElement>(
      `[data-public-search-tour-target="${currentStep.target}"]`,
    );
    if (target === null) {
      setGeometry(null);
      return;
    }

    const targetRect = target.getBoundingClientRect();
    if (targetRect.height === 0 || targetRect.width === 0) {
      setGeometry(null);
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tipWidth = Math.min(360, viewportWidth - VIEWPORT_GUTTER * 2);
    const spotlight = {
      top: targetRect.top - TARGET_PADDING,
      left: targetRect.left - TARGET_PADDING,
      width: targetRect.width + TARGET_PADDING * 2,
      height: targetRect.height + TARGET_PADDING * 2,
    };

    const triggerRect = document
      .querySelector<HTMLElement>("[data-public-search-tour-trigger]")
      ?.getBoundingClientRect();
    const triggerIsAdjacent =
      triggerRect !== undefined &&
      triggerRect.height > 0 &&
      triggerRect.top < spotlight.top &&
      triggerRect.bottom > spotlight.top - TIP_GAP - 60;
    const blockTop = triggerIsAdjacent ? triggerRect.top : spotlight.top;
    const spaceBelow =
      viewportHeight -
      (spotlight.top + spotlight.height + TIP_GAP) -
      VIEWPORT_GUTTER;
    const spaceAbove = blockTop - TIP_GAP - VIEWPORT_GUTTER;
    const defaultLeft = Math.min(
      Math.max(VIEWPORT_GUTTER, spotlight.left + TARGET_PADDING),
      Math.max(VIEWPORT_GUTTER, viewportWidth - tipWidth - VIEWPORT_GUTTER),
    );

    let top: number;
    let left = defaultLeft;
    let height: number;
    let align: TourGeometry["tip"]["align"];
    let placement: TourGeometry["tip"]["placement"];

    if (spaceBelow >= MIN_VERTICAL_BAND) {
      top = spotlight.top + spotlight.height + TIP_GAP;
      height = spaceBelow;
      align = "flex-start";
      placement = "below";
    } else if (spaceAbove >= MIN_VERTICAL_BAND) {
      top = VIEWPORT_GUTTER;
      height = spaceAbove;
      align = "flex-end";
      placement = "above";
    } else {
      const right = spotlight.left + spotlight.width + TIP_GAP;
      const leftCandidate = spotlight.left - TIP_GAP - tipWidth;
      if (right + tipWidth <= viewportWidth - VIEWPORT_GUTTER) left = right;
      else if (leftCandidate >= VIEWPORT_GUTTER) left = leftCandidate;
      top = VIEWPORT_GUTTER;
      height = Math.max(1, viewportHeight - VIEWPORT_GUTTER * 2);
      align = "center";
      placement = "side";
    }

    const arrowLeft = Math.min(
      Math.max(20, spotlight.left + spotlight.width / 2 - left - 6),
      Math.max(20, tipWidth - 32),
    );

    setGeometry({
      spotlight,
      tip: { top, left, width: tipWidth, height, align, placement, arrowLeft },
    });
  }, [open, step]);

  useEffect(() => {
    let hostFlow = false;
    try {
      hostFlow = new URLSearchParams(window.location.search).get("fluxo") === "criar";
    } catch {
      // An invalid location must not prevent the manual walkthrough.
    }

    try {
      if (hostFlow || window.localStorage.getItem(WALKTHROUGH_STORAGE_KEY) !== null) {
        return;
      }
    } catch {
      if (hostFlow) return;
    }

    autoOpenTimerRef.current = window.setTimeout(() => {
      autoOpenTimerRef.current = null;
      preparingStepRef.current = true;
      setGeometry(null);
      setStep(0);
      setOpen(true);
    }, 700);

    return clearAutoOpenTimer;
  }, [clearAutoOpenTimer]);

  useEffect(() => {
    if (!open) return;
    const version = ++preparationVersionRef.current;
    preparingStepRef.current = true;
    let cancelled = false;

    const prepareStep = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        await afterAnimationFrame();
        if (cancelled || version !== preparationVersionRef.current) return;
        const scrollTarget = getStepScrollTarget(step);
        if (scrollTarget === null) break;
        await scrollToPosition(scrollTarget);
        if (cancelled || version !== preparationVersionRef.current) return;
        await afterAnimationFrame();
        await afterAnimationFrame();
      }
      if (cancelled || version !== preparationVersionRef.current) return;
      preparingStepRef.current = false;
      measure();
    };

    void prepareStep();
    return () => {
      cancelled = true;
      preparationVersionRef.current += 1;
      cancelProgrammaticScroll();
    };
  }, [cancelProgrammaticScroll, measure, open, scrollToPosition, step]);

  useEffect(() => {
    if (!open) return;

    let animationFrame: number | null = null;
    const scheduleMeasure = () => {
      if (preparingStepRef.current) return;
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        measure();
      });
    };
    const timers = [40, 120, 300, 560].map((delay) =>
      window.setTimeout(scheduleMeasure, delay),
    );
    scheduleMeasure();
    const target = document.querySelector<HTMLElement>(
      `[data-public-search-tour-target="${tourSteps[step].target}"]`,
    );
    const trigger = document.querySelector<HTMLElement>(
      "[data-public-search-tour-trigger]",
    );
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(scheduleMeasure);
    if (target !== null) resizeObserver?.observe(target);
    if (trigger !== null) resizeObserver?.observe(trigger);
    if (panelRef.current !== null) resizeObserver?.observe(panelRef.current);
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, true);

    return () => {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      timers.forEach((timer) => window.clearTimeout(timer));
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, true);
    };
  }, [measure, open, step]);

  const currentStep = tourSteps[step];
  const geometryReady = geometry !== null;

  useEffect(() => {
    if (!open || !geometryReady) return;
    const animationFrame = window.requestAnimationFrame(() => titleRef.current?.focus());
    return () => window.cancelAnimationFrame(animationFrame);
  }, [geometryReady, open, step]);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        clearAutoOpenTimer();
        if (nextOpen) {
          cancelProgrammaticScroll();
          preparingStepRef.current = true;
          setGeometry(null);
          setStep(0);
          setOpen(true);
          return;
        }
        closeTour();
      }}
    >
      <DialogPrimitive.Trigger asChild>
        <Button
          className="tb-public-search-tour-trigger"
          data-public-search-tour-trigger="true"
          type="button"
          variant="link"
        >
          Entenda como buscar
        </Button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="tb-public-search-tour-backdrop"
          data-ready={geometryReady}
        />
        {geometry !== null && (
          <div
            aria-hidden="true"
            className="tb-public-search-tour-spotlight"
            data-step={step + 1}
            style={geometry.spotlight}
          />
        )}
        <DialogPrimitive.Content
          aria-busy={!geometryReady}
          className="tb-public-search-tour"
          data-placement={geometry?.tip.placement}
          data-ready={geometryReady}
          data-step={step + 1}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (!geometryReady) event.preventDefault();
          }}
          style={
            geometry === null
              ? undefined
              : {
                  alignItems: geometry.tip.align,
                  height: geometry.tip.height,
                  left: geometry.tip.left,
                  top: geometry.tip.top,
                  visibility: "visible",
                  width: geometry.tip.width,
                }
          }
        >
          {geometry?.tip.placement === "below" && (
            <span
              aria-hidden="true"
              className="tb-public-search-tour-arrow tb-public-search-tour-arrow-up"
              style={{ left: geometry.tip.arrowLeft }}
            />
          )}
          {geometry?.tip.placement === "above" && (
            <span
              aria-hidden="true"
              className="tb-public-search-tour-arrow tb-public-search-tour-arrow-down"
              style={{ left: geometry.tip.arrowLeft }}
            />
          )}
          <div ref={panelRef} className="tb-public-search-tour-panel">
            <div aria-hidden="true" className="tb-public-search-tour-progress">
              {tourSteps.map((tourStep, index) => (
                <span data-active={index <= step} key={tourStep.target} />
              ))}
            </div>
            <div className="tb-public-search-tour-flow" key={currentStep.target}>
              <p className="tb-public-search-tour-eyebrow">{currentStep.eyebrow}</p>
              <DialogPrimitive.Title
                className="tb-public-search-tour-title"
                ref={titleRef}
                tabIndex={-1}
              >
                {currentStep.title} <em>{currentStep.emphasis}</em>
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="tb-public-search-tour-description">
                {currentStep.description}
              </DialogPrimitive.Description>
            </div>
            <div className="tb-public-search-tour-actions">
              <button
                className="tb-public-search-tour-skip"
                disabled={!geometryReady}
                onClick={closeTour}
                type="button"
              >
                Pular
              </button>
              <button
                className="tb-public-search-tour-next"
                disabled={!geometryReady}
                onClick={() => {
                  if (preparingStepRef.current || !geometryReady) return;
                  if (step === tourSteps.length - 1) {
                    closeTour();
                    return;
                  }
                  preparingStepRef.current = true;
                  cancelProgrammaticScroll();
                  setGeometry(null);
                  setStep((current) =>
                    Math.min(current + 1, tourSteps.length - 1),
                  );
                }}
                type="button"
              >
                {step === tourSteps.length - 1 ? "Concluir" : "Continuar"}
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
